import { useState, useRef, useCallback, useEffect } from 'react';
import { 
  RecordingState, 
  getAudioConstraints, 
  audioToBase64, 
  calculateVolume,
  isAudioRecordingSupported,
  getSupportedAudioTypes,
  AUDIO_ERRORS 
} from './audioUtils';

export interface VoiceRecordingHook {
  // State
  state: RecordingState;
  audioBlob: Blob | null;
  
  // Actions
  startRecording: () => Promise<void>;
  stopRecording: () => void;
  resetRecording: () => void;
  
  // Utils
  isSupported: boolean;
  supportedTypes: string[];
}

export const useVoiceRecording = (): VoiceRecordingHook => {
  // Recording state
  const [state, setState] = useState<RecordingState>({
    isRecording: false,
    duration: 0,
    volume: 0,
    error: null
  });
  
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  
  // Refs for recording management
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const durationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const volumeAnimationRef = useRef<number | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const isRecordingRef = useRef<boolean>(false); // Track recording state in ref to prevent infinite loops

  // Check browser support
  const isSupported = isAudioRecordingSupported();
  const supportedTypes = getSupportedAudioTypes();

  // Update volume visualization
  const updateVolume = useCallback(() => {
    if (!analyserRef.current || !isRecordingRef.current) return;

    const dataArray = new Float32Array(analyserRef.current.frequencyBinCount);
    analyserRef.current.getFloatTimeDomainData(dataArray);
    
    const volume = calculateVolume(dataArray);
    setState(prev => ({ ...prev, volume }));

    // Continue animation only if still recording
    if (isRecordingRef.current) {
      volumeAnimationRef.current = requestAnimationFrame(updateVolume);
    }
  }, []); // Remove state.isRecording from dependency array to prevent infinite loops

  // Start recording
  const startRecording = useCallback(async () => {
    try {
      if (!isSupported) {
        throw new Error(AUDIO_ERRORS.NOT_SUPPORTED);
      }

      // Reset state
      setState(prev => ({ ...prev, error: null }));
      setAudioBlob(null);
      audioChunksRef.current = [];

      // Get user media
      const stream = await navigator.mediaDevices.getUserMedia(getAudioConstraints());
      streamRef.current = stream;

      // Set up audio context for volume visualization
      audioContextRef.current = new AudioContext();
      const source = audioContextRef.current.createMediaStreamSource(stream);
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;
      source.connect(analyserRef.current);

      // Set up MediaRecorder
      const mimeType = supportedTypes[0] || 'audio/webm';
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType });

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        setAudioBlob(blob);
        
        // Clean up
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }
        if (audioContextRef.current) {
          audioContextRef.current.close();
          audioContextRef.current = null;
        }
        analyserRef.current = null;
      };

      // Start recording
      mediaRecorderRef.current.start(100); // Collect data every 100ms
      isRecordingRef.current = true; // Update ref

      setState(prev => ({ 
        ...prev, 
        isRecording: true, 
        duration: 0,
        volume: 0 
      }));

      // Start duration timer
      durationIntervalRef.current = setInterval(() => {
        setState(prev => ({ ...prev, duration: prev.duration + 1 }));
      }, 1000);

      // Start volume animation
      updateVolume();

    } catch (error) {
      let errorMessage = AUDIO_ERRORS.RECORDING_FAILED;
      
      if (error instanceof Error) {
        if (error.name === 'NotAllowedError') {
          errorMessage = AUDIO_ERRORS.PERMISSION_DENIED;
        } else if (error.name === 'NotFoundError') {
          errorMessage = AUDIO_ERRORS.NO_MICROPHONE;
        } else {
          errorMessage = error.message;
        }
      }

      setState(prev => ({ 
        ...prev, 
        error: errorMessage,
        isRecording: false 
      }));
      isRecordingRef.current = false; // Update ref
    }
  }, [isSupported, supportedTypes, updateVolume]);

  // Stop recording
  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecordingRef.current) {
      mediaRecorderRef.current.stop();
      isRecordingRef.current = false; // Update ref
      
      // Clear timers and animations
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
        durationIntervalRef.current = null;
      }
      
      if (volumeAnimationRef.current) {
        cancelAnimationFrame(volumeAnimationRef.current);
        volumeAnimationRef.current = null;
      }

      setState(prev => ({ 
        ...prev, 
        isRecording: false, 
        volume: 0 
      }));
    }
  }, []);

  // Reset recording
  const resetRecording = useCallback(() => {
    stopRecording();
    setAudioBlob(null);
    isRecordingRef.current = false; // Update ref
    setState({
      isRecording: false,
      duration: 0,
      volume: 0,
      error: null
    });
  }, [stopRecording]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopRecording();
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, [stopRecording]);

  return {
    state,
    audioBlob,
    startRecording,
    stopRecording,
    resetRecording,
    isSupported,
    supportedTypes
  };
}; 